// AuthGate — wraps any app route with authentication.
//
// When REACT_APP_SUPABASE_URL + REACT_APP_SUPABASE_ANON_KEY are set:
//   → full Supabase magic-link auth (email OTP)
//   → upserts a profile row on first sign-in
//   → sets BETA_SECRET in localStorage so existing app gates pass
//
// When those env vars are absent (local dev / pre-migration):
//   → falls back to the shared BETA_SECRET localStorage gate

import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import Logo from "../seller/components/Logo";

const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "sixzero2026";
const BETA_KEY    = "od_beta_token";
const ID_KEY      = "sz_identity";

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ─── CSS ─────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Outfit:wght@400;600;700&family=Playfair+Display:ital,wght@0,700;1,700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{margin:0;background:#0d1710;font-family:'Outfit',sans-serif;color:#fff}
  @keyframes ag-in{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes ag-spin{to{transform:rotate(360deg)}}
  @keyframes ag-pulse{0%,100%{opacity:0.4}50%{opacity:1}}
  .ag-input{
    width:100%;background:rgba(255,255,255,0.05);
    border:1.5px solid rgba(126,207,176,0.18);border-radius:10px;
    padding:13px 16px;font-size:15px;color:#fff;
    font-family:'Outfit',sans-serif;outline:none;transition:border-color 0.15s;
  }
  .ag-input::placeholder{color:rgba(255,255,255,0.22)}
  .ag-input:focus{border-color:rgba(126,207,176,0.5)}
  .ag-btn{
    width:100%;background:#2d7a62;color:#fff;border:none;
    border-radius:10px;padding:14px;font-size:15px;font-weight:700;
    cursor:pointer;font-family:'Outfit',sans-serif;transition:background 0.15s;
  }
  .ag-btn:hover:not(:disabled){background:#1c5448}
  .ag-btn:disabled{opacity:0.5;cursor:default}
  .ag-spin{width:18px;height:18px;border:2px solid rgba(126,207,176,0.2);border-top-color:#7ecfb0;border-radius:50%;animation:ag-spin 0.7s linear infinite}
`;

// ─── Shared shell ─────────────────────────────────────────────────────────────
function Shell({ children }) {
  return (
    <>
      <style>{CSS}</style>
      <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 24px", background:"radial-gradient(ellipse 80% 60% at 50% 0%, rgba(45,122,98,0.16) 0%, transparent 70%), #0d1710" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:44, animation:"ag-in 0.35s ease both" }}>
          <Logo size={26} />
          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:14, fontWeight:700, color:"#7ecfb0", letterSpacing:"0.22em" }}>6zero.ai</span>
        </div>
        <div style={{ width:"100%", maxWidth:380, animation:"ag-in 0.4s ease 0.05s both" }}>
          {children}
        </div>
      </div>
    </>
  );
}

// ─── Spinner (session loading) ────────────────────────────────────────────────
function Spinner() {
  return (
    <Shell>
      <div style={{ display:"flex", justifyContent:"center" }}>
        <div className="ag-spin" />
      </div>
    </Shell>
  );
}

// ─── Magic-link screen ────────────────────────────────────────────────────────
function MagicLinkScreen({ role }) {
  const params    = new URLSearchParams(window.location.search);
  const [email, setEmail]   = useState(params.get("email") || "");
  const [name, setName]     = useState("");
  const [sent, setSent]     = useState(false);
  const [loading, setLoad]  = useState(false);
  const [err, setErr]       = useState("");

  // Preserve the exact path the user signed in from (e.g. /sell/v2, not just
  // /sell) so auth redirects don't silently drop them into a different view.
  const basePath   = window.location.pathname;
  const brokerId   = params.get("broker");
  const redirectTo = window.location.origin + basePath + (brokerId ? `?broker=${brokerId}` : "");

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoad(true); setErr("");
    // Store name optimistically so it's available after redirect.
    if (name.trim()) {
      try { localStorage.setItem(ID_KEY, JSON.stringify({ name: name.trim(), email: email.trim() })); } catch {}
    }
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: redirectTo, data: { name: name.trim(), role } },
    });
    setLoad(false);
    if (error) { setErr(error.message); return; }
    setSent(true);
  };

  const roleLabel = role === "broker" ? "broker portal" : role === "seller" ? "seller dashboard" : role === "buyer" ? "buyer search" : role === "vendor" ? "vendor portal" : "account";

  if (sent) {
    return (
      <Shell>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:16 }}>✉</div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, marginBottom:10, color:"#fff" }}>Check your inbox</h2>
          <p style={{ fontSize:14, color:"rgba(255,255,255,0.45)", lineHeight:1.7, marginBottom:24 }}>
            We sent a magic link to <strong style={{ color:"#7ecfb0" }}>{email}</strong>.<br />Click it to sign in — no password needed.
          </p>
          <button onClick={() => setSent(false)} style={{ background:"none", border:"none", fontFamily:"'DM Mono',monospace", fontSize:11, color:"rgba(255,255,255,0.3)", cursor:"pointer", letterSpacing:"0.08em" }}>
            wrong email? go back
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(26px,5vw,34px)", fontWeight:700, lineHeight:1.1, marginBottom:8, color:"#fff", textAlign:"center" }}>
        Sign in to<br /><em style={{ color:"#7ecfb0" }}>6zero.ai</em>
      </h1>
      <p style={{ fontSize:13, color:"rgba(255,255,255,0.35)", textAlign:"center", lineHeight:1.6, marginBottom:28 }}>
        Enter your email to access your {roleLabel}.
      </p>
      <form onSubmit={send} style={{ display:"flex", flexDirection:"column", gap:10 }}>
        <input className="ag-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
        <input className="ag-input" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErr(""); }} placeholder="your@email.com" autoFocus required />
        <button className="ag-btn" type="submit" disabled={loading || !email.trim()}>
          {loading ? <div style={{ display:"flex", justifyContent:"center" }}><div className="ag-spin" /></div> : "Send magic link →"}
        </button>
        {err && <p style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:"#e07070", textAlign:"center", letterSpacing:"0.06em" }}>{err}</p>}
      </form>
      <p style={{ fontSize:11, color:"rgba(255,255,255,0.18)", textAlign:"center", marginTop:20, fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>
        No account needed — we'll create one on your first sign-in.
      </p>
    </Shell>
  );
}

// ─── Beta-secret fallback (no Supabase configured) ───────────────────────────
function BetaGate({ role, children }) {
  const ok = typeof window !== "undefined" && localStorage.getItem(BETA_KEY) === BETA_SECRET;
  if (!ok) { window.location.href = "/public"; return null; }
  const identity = (() => { try { return JSON.parse(localStorage.getItem(ID_KEY) || "null"); } catch { return null; } })();
  return (
    <AuthContext.Provider value={{ user: null, profile: identity, role, mode: "beta" }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Supabase auth inner (only rendered when supabase is configured) ──────────
function SupabaseGate({ role, children }) {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const syncProfile = async (user) => {
    try { localStorage.setItem(BETA_KEY, BETA_SECRET); } catch {}
    const localId = (() => { try { return JSON.parse(localStorage.getItem(ID_KEY) || "null"); } catch { return null; } })();
    // Broker is a professional identity, not a consumer "mode" — buyer/
    // seller are allowed to flip based on whichever route someone's on
    // (deliberate — a seller can also browse as a buyer), but a broker
    // landing on /buy or /sell for any reason (stray link, old tab, a
    // future auth-redirect misconfig) must never silently downgrade them.
    // Once broker, always broker until explicitly changed elsewhere.
    const { data: existing } = await supabase.from("profiles").select("role").eq("id", user.id).maybeSingle();

    // RBAC gate: broker role is no longer self-service. Anyone signing in
    // via /broker for the first time (existing role isn't already broker)
    // must have their email on broker_allowlist (admin-managed) — otherwise
    // they get an access-denied screen, never the portal.
    if (role === "broker" && existing?.role !== "broker") {
      const { data: allowed } = await supabase.from("broker_allowlist").select("email").eq("email", user.email.toLowerCase()).maybeSingle();
      if (!allowed) { setAccessDenied(true); return; }
    }

    const finalRole = existing?.role === "broker" ? "broker" : role;
    const { data } = await supabase.from("profiles").upsert({
      id: user.id,
      email: user.email,
      name: localId?.name || user.user_metadata?.name || null,
      role: finalRole,
      updated_at: new Date().toISOString(),
    }, { onConflict: "id" }).select().single();
    setProfile(data);

    // Broker identity is protected (see above) — but the ROUTE itself
    // still isn't, so redirect away from a mismatched consumer page
    // rather than silently showing it under a broker session.
    if (finalRole === "broker" && role !== "broker") {
      window.location.replace("/broker");
      return;
    }

    // If arriving via broker invite link, create the broker-client link.
    const sp = new URLSearchParams(window.location.search);
    const brokerId = sp.get("broker");
    if (brokerId && brokerId !== user.id) {
      await supabase.from("broker_client_links").upsert(
        { broker_id: brokerId, client_id: user.id },
        { onConflict: "broker_id,client_id" }
      );
      // Remove ?broker= so refreshing doesn't re-run this.
      const clean = new URL(window.location.href);
      clean.searchParams.delete("broker");
      window.history.replaceState({}, "", clean.toString());
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      if (data.session) syncProfile(data.session.user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_ev, s) => {
      setSession(s ?? null);
      if (s) syncProfile(s.user);
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  if (session === undefined) return <Spinner />;
  if (!session) return <MagicLinkScreen role={role} />;
  if (accessDenied) {
    return (
      <Shell>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, marginBottom: 10, color: "#fff" }}>Broker access is by invitation</h2>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.45)", lineHeight: 1.7, marginBottom: 24 }}>
            This email isn't on the broker allowlist yet. Reach out to your 6zero.ai contact to get added.
          </p>
          <button onClick={() => (window.location.href = "/")} style={{ background: "none", border: "none", fontFamily: "'DM Mono',monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", cursor: "pointer", letterSpacing: "0.08em" }}>
            ← back to 6zero.ai
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <AuthContext.Provider value={{ user: session.user, profile, role, mode: "supabase" }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
export default function AuthGate({ role, children }) {
  if (!supabase) return <BetaGate role={role}>{children}</BetaGate>;
  return <SupabaseGate role={role}>{children}</SupabaseGate>;
}
